import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SupportClient, { CreateSupportRequest, SupportRequestType } from '../apiClients/SupportClient';
import CreateSupportRequestDialog from '../components/support/CreateSupportRequestDialog';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { Securables } from '../Enums';
import useViewportMode from '../hooks/useViewportMode';
import {
  getContextHelpArticles,
  getVisibleHelpArticles,
  resolveHelpRoute,
  searchHelpArticles,
} from './catalog';
import HelpArticleCard from './HelpArticleCard';
import { HelpArticle } from './types';

const HelpLauncher = () => {
  const history = useHistory();
  const location = useLocation();
  const { hasRole } = useAccessInfo();
  const { mode, isPhone, isTablet, isLandscape } = useViewportMode();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestType, setRequestType] = useState<SupportRequestType>('Issue');
  const [pendingRequestType, setPendingRequestType] = useState<SupportRequestType>();
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const route = resolveHelpRoute(location.pathname);
  const hasCapability = (capability: string) => hasRole(capability as Securables, capability === Securables.synergist_admin);
  const visibleArticles = getVisibleHelpArticles(hasCapability);
  const contextual = getContextHelpArticles(visibleArticles, route.id);
  const shown = query ? searchHelpArticles(visibleArticles, query, route.id) : contextual.length ? contextual : visibleArticles.slice(0, 5);

  const openArticle = (article: HelpArticle) => {
    setOpen(false);
    history.push(`/Help?article=${encodeURIComponent(article.slug)}`);
  };

  const openHelpCenter = () => {
    setOpen(false);
    history.push(`/Help?route=${encodeURIComponent(route.id)}`);
  };

  const startRequest = (type: SupportRequestType) => {
    setRequestError('');
    setPendingRequestType(type);
    setOpen(false);
  };

  const finishDrawerClose = () => {
    if (!pendingRequestType) return;
    setRequestType(pendingRequestType);
    setPendingRequestType(undefined);
    setRequestOpen(true);
  };

  const submitRequest = async (request: CreateSupportRequest) => {
    try {
      setRequestBusy(true);
      setRequestError('');
      await new SupportClient().CreateRequest(request);
      setRequestOpen(false);
      setRequestSubmitted(true);
    } catch {
      setRequestError('The request could not be submitted. Your draft is still open so you can try again.');
    } finally {
      setRequestBusy(false);
    }
  };

  const drawerWidth = isPhone ? '100vw' : isTablet ? (isLandscape ? 520 : 480) : 440;

  return <>
    <Tooltip title="Help for this page">
      <IconButton
        color="inherit"
        aria-label="Open Help Center"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        sx={{ width: 44, height: 44 }}>
        <HelpOutlineRoundedIcon />
      </IconButton>
    </Tooltip>
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      SlideProps={{ onExited: finishDrawerClose }}
      PaperProps={{
        'data-testid': 'context-help-drawer',
        'data-viewport-mode': mode,
        sx: {
          width: drawerWidth,
          maxWidth: '100vw',
          height: isPhone ? '100dvh' : '100%',
          pb: 'env(safe-area-inset-bottom)',
        },
      } as any}>
      <Stack sx={{ height: '100%', minHeight: 0 }}>
        <Box sx={{ p: { xs: 2, sm: 2.5 }, pt: 'max(env(safe-area-inset-top), 16px)' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary">Help for this page</Typography>
              <Typography variant="h5" noWrap>{route.label}</Typography>
            </Box>
            <IconButton aria-label="Close Help Center" onClick={() => setOpen(false)} sx={{ width: 44, height: 44 }}><CloseRoundedIcon /></IconButton>
          </Stack>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search Help Center"
            inputProps={{ 'aria-label': 'Search Help Center' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
            sx={{ mt: 2 }}
          />
        </Box>
        <Divider />
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          <Stack spacing={1.5}>
            {shown.map(article => <HelpArticleCard key={article.id} article={article} onOpen={openArticle} compact />)}
            {shown.length === 0 && <Alert severity="info">No articles match that search. Try a shorter phrase or contact support.</Alert>}
          </Stack>
        </Box>
        <Divider />
        <Stack spacing={1} sx={{ p: 2 }}>
          <Button variant="contained" startIcon={<OpenInFullRoundedIcon />} onClick={openHelpCenter} sx={{ minHeight: 44 }}>Open full Help Center</Button>
          <Stack direction="row" spacing={1}>
            <Button fullWidth startIcon={<BugReportOutlinedIcon />} onClick={() => startRequest('Issue')} sx={{ minHeight: 44 }}>Issue</Button>
            <Button fullWidth startIcon={<LightbulbOutlinedIcon />} onClick={() => startRequest('FeatureRequest')} sx={{ minHeight: 44 }}>Feature</Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
    <CreateSupportRequestDialog open={requestOpen} initialType={requestType} routeId={route.id} busy={requestBusy} error={requestError} onClose={() => setRequestOpen(false)} onSubmit={submitRequest} />
    <Snackbar open={requestSubmitted} autoHideDuration={5000} onClose={() => setRequestSubmitted(false)} message="Support request submitted" />
  </>;
};

export default HelpLauncher;
